import React, { useEffect } from 'react';

import { StyleProp, StyleSheet, ViewStyle } from 'react-native';

import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withRepeat,
    withSequence,
    withTiming,
} from 'react-native-reanimated';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { SvgProps } from 'react-native-svg';

import { useRoute } from '@react-navigation/native';

import { SCREEN } from 'router/screens';

import { isTablet, isWeb } from 'utils/responsiveness';

import styles from './styles';

type Props = {
    SkeletonLoadingComponent: React.FC<SvgProps>;
    backgroundColor?: string;
    width?: number | string;
    height?: number | string;
    animatedBlockStyle?: StyleProp<ViewStyle>;
    svgStyle?: StyleProp<ViewStyle>;
    edges?: Edge[];
    isScreenSkeleton?: boolean;
    testID?: string;
};

function SkeletonLoading({
    backgroundColor,
    width,
    height,
    animatedBlockStyle,
    svgStyle,
    edges,
    SkeletonLoadingComponent,
    isScreenSkeleton = false,
    testID,
}: Props) {
    const opacitySharedValue = useSharedValue(1);
    const route = useRoute();

    const animatedStyle = useAnimatedStyle(() => ({
        opacity: opacitySharedValue.value,
    }));

    const mobileViewBox = isTablet ? '0 0 400 296' : '0 0 360 304';
    const viewBox = isWeb ? '0 0 400 276' : mobileViewBox;
    const isDetailsScreen = route.name === SCREEN.DETAILS;

    useEffect(() => {
        opacitySharedValue.value = withRepeat(
            withSequence(
                withTiming(0.3, { duration: 600 }),
                withDelay(100, withTiming(1, { duration: 600 })),
            ),
            100,
        );
    }, []);

    return (
        <SafeAreaView
            edges={edges}
            style={[
                isScreenSkeleton && StyleSheet.absoluteFill,
                {
                    backgroundColor,
                },
            ]}
        >
            <Animated.View
                style={[styles.root, animatedBlockStyle, animatedStyle]}
                testID={testID}
            >
                <SkeletonLoadingComponent
                    width={width}
                    height={height}
                    style={StyleSheet.flatten([svgStyle, { backgroundColor }])}
                    {...(isDetailsScreen && { viewBox })}
                />
            </Animated.View>
        </SafeAreaView>
    );
}

export default SkeletonLoading;
